import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { mockClientesAsesoria } from '../mocks/clientesAsesoria';

export interface ClienteAsesoria {
  id: number;
  nombre: string;
  email: string | null;
  telefono: string | null;
  plan_contratado: string | null;
  fecha_inicio: string | null;
  notas: string | null;
  activo: boolean;
  created_at?: string;
}

export function useAdminClientesAsesoria() {
  const [data, setData] = useState<ClienteAsesoria[]>(mockClientesAsesoria);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    if (hasFetched) return;
    const fetchData = async () => {
      const timeoutId = setTimeout(() => {
        setLoading(false);
        setError('Tiempo de espera. Usando datos locales.');
      }, 3000);
      try {
        const { data: rows, error: err } = await supabase
          .from('clientes_asesoria')
          .select('*')
          .order('id', { ascending: true });
        clearTimeout(timeoutId);
        if (err) {
          setError(err.message);
          setData(mockClientesAsesoria);
        } else if (rows && rows.length > 0) {
          setData(rows as ClienteAsesoria[]);
        } else {
          setData(mockClientesAsesoria);
        }
      } catch {
        clearTimeout(timeoutId);
        setError('Error de conexión. Usando datos locales.');
        setData(mockClientesAsesoria);
      } finally {
        setLoading(false);
        setHasFetched(true);
      }
    };
    fetchData();
  }, [hasFetched]);

  const add = async (item: Omit<ClienteAsesoria, 'id' | 'created_at'>) => {
    setLoading(true);
    try {
      const { data: inserted, error: err } = await supabase
        .from('clientes_asesoria')
        .insert(item)
        .select()
        .single();
      if (err) { setError(err.message); return; }
      if (inserted) setData((prev) => [...prev, inserted as ClienteAsesoria]);
    } catch { setError('Error al crear'); }
    finally { setLoading(false); }
  };

  const update = async (id: number, updates: Partial<ClienteAsesoria>) => {
    setLoading(true);
    try {
      const { data: updated, error: err } = await supabase
        .from('clientes_asesoria')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (err) { setError(err.message); return; }
      if (updated) setData((prev) => prev.map((i) => (i.id === id ? (updated as ClienteAsesoria) : i)));
    } catch { setError('Error al actualizar'); }
    finally { setLoading(false); }
  };

  const remove = async (id: number) => {
    setLoading(true);
    try {
      const { error: err } = await supabase.from('clientes_asesoria').delete().eq('id', id);
      if (err) { setError(err.message); return; }
      setData((prev) => prev.filter((i) => i.id !== id));
    } catch { setError('Error al eliminar'); }
    finally { setLoading(false); }
  };

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: rows, error: err } = await supabase.from('clientes_asesoria').select('*').order('id', { ascending: true });
      if (err) setError(err.message);
      else if (rows) setData(rows as ClienteAsesoria[]);
    } catch { setError('Error de conexión'); }
    finally { setLoading(false); }
  };

  return { data, add, update, remove, refresh, loading, error };
}